export type ImportCompleteResult = {
  error?: string;
  success: boolean;
};

type Waiter = {
  resolve: (result: ImportCompleteResult) => void;
};

export const DEFAULT_IMPORT_TIMEOUT_MS = 5 * 60 * 1000;
export const BATCH_IMPORT_TIMEOUT_MS = 30 * 60 * 1000;

const waitersByFileName = new Map<string, Waiter[]>();

/**
 * Resolves when the UXP host reports ImportComplete for `fileName`.
 * usePremiereImportLifecycle calls {@link resolveImportComplete} from its message handler.
 *
 * Callers must register via `waitForImportComplete` before dispatching to the host
 * so a matching waiter exists when ImportComplete arrives.
 */
export const waitForImportComplete = (
  fileName: string,
  timeoutMs = DEFAULT_IMPORT_TIMEOUT_MS,
): Promise<ImportCompleteResult> => {
  return new Promise((resolve) => {
    const queue = waitersByFileName.get(fileName) ?? [];

    const waiter: Waiter = {
      resolve: (result) => {
        clearTimeout(timeoutId);
        resolve(result);
      },
    };

    queue.push(waiter);
    waitersByFileName.set(fileName, queue);

    const timeoutId = setTimeout(() => {
      const currentQueue = waitersByFileName.get(fileName);
      const index = currentQueue?.indexOf(waiter) ?? -1;

      if (index === -1 || currentQueue == null) {
        return;
      }

      const nextQueue = currentQueue.filter((entry) => entry !== waiter);

      if (nextQueue.length === 0) {
        waitersByFileName.delete(fileName);
      } else {
        waitersByFileName.set(fileName, nextQueue);
      }

      resolve({ error: 'Import timed out', success: false });
    }, timeoutMs);
  });
};

export const resolveImportComplete = (fileName: string, result: ImportCompleteResult): void => {
  const queue = waitersByFileName.get(fileName);

  if (!queue?.length) {
    return;
  }

  const [waiter, ...rest] = queue;

  if (rest.length === 0) {
    waitersByFileName.delete(fileName);
  } else {
    waitersByFileName.set(fileName, rest);
  }

  waiter.resolve(result);
};

/** Test-only reset — not used in production. */
export const resetImportCompletionWaiters = (): void => {
  waitersByFileName.clear();
};
